import { Smartphone, Speaker, Video, Sliders } from "lucide-react";

export const PRICING_MULTIPLIERS = {
  SET_CORE: 0.3,
  SET_AUDIO: 1.5,
  SET_VIDEO: 2.875
} as const;

export const SET_CORE_FLOOR_PRICE_PER_SHOW = 1999; // Minimum price per show for SET Core package

export type PackageType = 'set-core' | 'set-audio' | 'set-video' | 'custom';

export interface PackageFeature {
  text: string;
  highlight?: boolean;
  isDynamic?: boolean;
  dynamicText?: (count: number) => string;
  details?: {
    title: string;
    description: string;
    imageUrl?: string;
  };
}

export interface PackageConfig {
  id: PackageType;
  name: string;
  subtitle: string;
  icon: React.ComponentType<{ className?: string }>;
  colorScheme: {
    primary: string;
    secondary: string;
    gradient?: string;
    buttonVariant?: 'default' | 'outline';
  };
  features: PackageFeature[];
  multiplier: number;
  badge?: string;
}

export const PACKAGE_CONFIGS: Record<PackageType, PackageConfig> = {
  'set-core': {
    id: 'set-core',
    name: 'SET Core',
    subtitle: 'Essential sponsorship foundation',
    icon: Smartphone,
    colorScheme: {
      primary: 'gray-600',
      secondary: 'gray-100',
      buttonVariant: 'outline'
    },
    features: [
      {
        text: 'Logo on SET experience',
        details: {
          title: 'Logo on SET Experience',
          description: 'Your brand will be the presenting sponsor of the in show experience with logo placement and brand mention.',
          imageUrl: '/images/examples/set-logo-example.png'
        }
      },
      {
        text: 'Fan lead capture & sharing',
        details: {
          title: 'Lead Capture & Sharing',
          description: 'Receive a portion of fan contact information captured during ticket purchase and entry for your marketing campaigns.',
          imageUrl: '/images/examples/lead-capture-example.png'
        }
      },
      { 
        text: 'Post check in branding',
        details: {
          title: 'Post Check In Branding',
          description: 'Send a targeted message to fans immediately after they check in at the venue.',
          imageUrl: '/images/examples/checkin-message-example.png'
        }
      },
      { 
        text: 'Post show branded email',
        details: {
          title: 'Post-Show Branded Email',
          description: 'Reach fans with a custom branded email after the show, perfect for driving engagement while the experience is fresh.',
          imageUrl: '/images/examples/post-show-email-example.png'
        }
      },
      {
        text: 'Post show report',
        details: {
          title: 'Post Show Report',
          description: 'Receive a comprehensive report after each show with key metrics, attendance data, and engagement statistics.',
          imageUrl: '/images/examples/post-show-report-example.png'
        }
      },
      {
        text: '2 tickets to each show',
        details: {
          title: 'Show Ticket Allocation',
          description: 'Receive 2 complimentary tickets to each sponsored show for your team or clients.',
          imageUrl: '/images/examples/tickets-example.png'
        }
      }
    ],
    multiplier: PRICING_MULTIPLIERS.SET_CORE,
    badge: 'In Hand'
  },
  'set-audio': {
    id: 'set-audio',
    name: 'SET Audio',
    subtitle: 'Audio-enhanced brand experience',
    icon: Speaker,
    colorScheme: {
      primary: 'blue-600',
      secondary: 'blue-100',
      gradient: 'from-blue-50 via-blue-100/50 to-blue-200/30',
      buttonVariant: 'default'
    },
    features: [
      { text: 'Everything in SET Core +', highlight: true },
      {
        text: 'In-show branded audio',
        details: {
          title: 'In-Show Branded Audio',
          description: 'Custom audio messages from your brand played at strategic moments during the show experience.',
          imageUrl: '/images/examples/branded-messaging-example.png'
        }
      },
      {
        text: 'Upgraded logo placement',
        details: {
          title: 'Upgraded Logo Placement',
          description: 'Premium logo placement within the SET.Live experience with enhanced visibility and prominent positioning.',
          imageUrl: '/images/examples/upgraded-logo-placement-example.png'
        }
      },
      {
        text: '2 additional tickets (4 total)',
        details: {
          title: 'Additional Tickets',
          description: 'Receive 4 complimentary tickets to each sponsored show, upgraded from the 2 tickets included in SET Core.',
          imageUrl: '/images/examples/vip-tickets-example.png'
        }
      },
      {
        text: 'Enhanced reporting & analytics',
        details: {
          title: 'Enhanced Reporting & Analytics',
          description: 'Get detailed analytics and insights including demographic breakdowns, engagement metrics, and ROI analysis for your sponsorship.',
          imageUrl: '/images/examples/enhanced-reporting-example.png'
        }
      },
      {
        text: 'Artist content rights (limited)',
        details: {
          title: 'Artist Content Rights',
          description: 'Limited rights to use artist content and performances in your marketing materials with specified usage restrictions.',
          imageUrl: '/images/examples/content-rights-example.png'
        }
      }
    ],
    multiplier: PRICING_MULTIPLIERS.SET_AUDIO,
    badge: 'In Room'
  },
  'set-video': {
    id: 'set-video',
    name: 'SET Video',
    subtitle: 'Video-powered premium partnership',
    icon: Video,
    colorScheme: {
      primary: 'purple-600',
      secondary: 'purple-100',
      gradient: 'from-purple-500/15 to-blue-500/15'
    },
    features: [
      { text: 'Everything in SET Audio +', highlight: true },
      {
        text: 'SET.Live takeover',
        details: {
          title: 'SET.Live Takeover',
          description: 'Deeper integration with the SET.Live platform including premium placement and extended brand visibility throughout the show experience.',
          imageUrl: '/images/examples/enhanced-set-example.png'
        }
      },
      {
        text: 'Artist content rights (full rights)',
        details: {
          title: 'Artist Content Rights',
          description: 'Full rights to use artist content, performances, and materials in your marketing campaigns with comprehensive usage permissions.',
          imageUrl: '/images/examples/content-rights-example.png'
        }
      },
      {
        text: 'In-venue branded video',
        details: {
          title: 'In-Venue Branded Video',
          description: 'Branded video content featuring your brand, displayed at strategic moments during the show experience.',
          imageUrl: '/images/examples/enhanced-branding-example.png'
        }
      },
      {
        text: 'Branded VIP experience',
        details: {
          title: 'Branded VIP Experience',
          description: 'Create a custom VIP experience area at the venue branded with your company, including exclusive perks and amenities.',
          imageUrl: '/images/examples/branded-vip-example.png'
        }
      }
    ],
    multiplier: PRICING_MULTIPLIERS.SET_VIDEO,
    badge: 'On Stage'
  },
  'custom': {
    id: 'custom',
    name: 'SET Custom',
    subtitle: 'Tailored sponsorship package',
    icon: Sliders,
    colorScheme: {
      primary: 'green-600',
      secondary: 'green-100',
      gradient: 'from-green-50 to-emerald-50',
      buttonVariant: 'default'
    },
    features: [], // Custom features are defined per-user
    multiplier: 1, // Custom packages don't use multiplier pricing
    badge: 'Custom'
  }
};

// Legacy package type mapping for backwards compatibility
const LEGACY_PACKAGE_MAP: Record<string, PackageType> = {
  'ga': 'set-core',
  'vip': 'set-audio',
  'all-access': 'set-video'
};

export function getPackageConfig(packageType: PackageType | string): PackageConfig {
  // Handle legacy package types
  const mappedType = LEGACY_PACKAGE_MAP[packageType] || packageType;

  // Return the config, or default to set-core if not found
  return PACKAGE_CONFIGS[mappedType as PackageType] || PACKAGE_CONFIGS['set-core'];
}

export function formatPrice(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

// ============================================================================
// PACKAGE DELIVERABLES - Single Source of Truth
// ============================================================================
// This is the authoritative list of ALL package deliverables across all tiers.
// Used by plan-deliverables.tsx and any other component displaying deliverables.
// Update this list to change deliverables across the entire application.

export interface DeliverableItem {
  name: string;
  category?: 'standard' | 'mobile' | 'venue' | 'media';
  details?: {
    title: string;
    description: string;
    imageUrl?: string;
  };
  setCore: { included: boolean; note?: string };
  setAudio: { included: boolean; note?: string };
  setVideo: { included: boolean; note?: string };
}

export const PACKAGE_DELIVERABLES: DeliverableItem[] = [
  {
    name: "Logo on SET experience",
    setCore: { included: true },
    setAudio: { included: true },
    setVideo: { included: true }
  },
  {
    name: "Fan lead capture & sharing",
    setCore: { included: true },
    setAudio: { included: true },
    setVideo: { included: true }
  },
  {
    name: "Post check-in branding",
    setCore: { included: true },
    setAudio: { included: true },
    setVideo: { included: true }
  },
  {
    name: "Post-show branded email",
    setCore: { included: true },
    setAudio: { included: true },
    setVideo: { included: true }
  },
  {
    name: "Post-show report",
    setCore: { included: true },
    setAudio: { included: true },
    setVideo: { included: true }
  },
  {
    name: "In-show branded audio",
    category: 'standard',
    setCore: { included: false },
    setAudio: { included: true },
    setVideo: { included: true }
  },
  {
    name: "Upgraded logo placement",
    category: 'standard',
    setCore: { included: false },
    setAudio: { included: true },
    setVideo: { included: true }
  },
  {
    name: "Show ticket allocation",
    setCore: { included: true, note: "2 per show" },
    setAudio: { included: true, note: "4 per show" },
    setVideo: { included: true, note: "4 per show" }
  },
  {
    name: "Enhanced reporting & analytics",
    category: 'standard',
    setCore: { included: false },
    setAudio: { included: true },
    setVideo: { included: true }
  },
  {
    name: "Artist social post",
    category: 'standard',
    setCore: { included: false },
    setAudio: { included: false },
    setVideo: { included: false }
  },
  {
    name: "SET.Live takeover",
    category: 'standard',
    setCore: { included: false },
    setAudio: { included: false },
    setVideo: { included: true }
  },
  {
    name: "Artist content rights",
    category: 'standard',
    setCore: { included: false },
    setAudio: { included: true, note: "Limited" },
    setVideo: { included: true, note: "Full rights" }
  },
  {
    name: "In-venue branded video",
    category: 'standard',
    setCore: { included: false },
    setAudio: { included: false },
    setVideo: { included: true }
  },
  {
    name: "Branded VIP experience",
    category: 'standard',
    setCore: { included: false },
    setAudio: { included: false },
    setVideo: { included: true }
  },
  // Optional Add-Ons (Mobile Experience)
  {
    name: "Upgraded logo placement",
    category: 'mobile',
    details: {
      title: "Upgraded Logo Placement",
      description: "Premium logo placement within the SET.Live experience with enhanced visibility and prominent positioning."
    },
    setCore: { included: false },
    setAudio: { included: false },
    setVideo: { included: false }
  },
  {
    name: "Featured SET.Live tab",
    category: 'mobile',
    details: {
      title: "Featured SET.Live Tab",
      description: "Get a dedicated branded tab in the SET.Live app featuring your content, offers, and messaging. Highly visible placement ensures fans engage with your brand throughout the show experience."
    },
    setCore: { included: false },
    setAudio: { included: false },
    setVideo: { included: false }
  },
  {
    name: "Exclusive brand offer",
    category: 'mobile',
    details: {
      title: "Exclusive Brand Offer",
      description: "Create an exclusive offer or promotion for fans delivered through the SET.Live app. Perfect for driving conversions with time-sensitive deals, coupons, or giveaways that fans can redeem during or after the show."
    },
    setCore: { included: false },
    setAudio: { included: false },
    setVideo: { included: false }
  },
  {
    name: "SET.Live takeover",
    category: 'mobile',
    details: {
      title: "SET.Live Takeover",
      description: "Transform the entire SET.Live mobile experience with your brand. Complete takeover includes custom splash screen, branded navigation, and prominent logo placement throughout the app during the show."
    },
    setCore: { included: false },
    setAudio: { included: false },
    setVideo: { included: false }
  },
  // Optional Add-Ons (In Show/Venue)
  {
    name: "In-venue branded audio",
    category: 'venue',
    details: {
      title: "In-Venue Branded Audio",
      description: "Broadcast your brand message through the venue's PA system before the show or during intermission. Reach thousands of fans with professional audio spots in a high-energy, captive audience environment."
    },
    setCore: { included: false },
    setAudio: { included: false },
    setVideo: { included: false }
  },
  {
    name: "Artist-voiced branded audio",
    category: 'venue',
    details: {
      title: "Artist-Voiced Branded Audio",
      description: "Have the artist personally voice your brand message for in-venue audio spots. This premium add-on creates authentic, powerful endorsements that resonate deeply with fans and elevate your brand association."
    },
    setCore: { included: false },
    setAudio: { included: false },
    setVideo: { included: false }
  },
  {
    name: "In-venue branded static image",
    category: 'venue',
    details: {
      title: "In-Venue Branded Static Image",
      description: "Display your branded graphics and still images on the main stage screens during key moments. Eye-catching visual presence that reaches every attendee with high-impact brand visibility."
    },
    setCore: { included: false },
    setAudio: { included: false },
    setVideo: { included: false }
  },
  {
    name: "In-venue branded video",
    category: 'venue',
    details: {
      title: "In-Venue Branded Video",
      description: "Showcase your brand's video content on the main stage screens. Play commercials, product demos, or branded entertainment that captures the attention of the entire audience on the biggest screen in the house."
    },
    setCore: { included: false },
    setAudio: { included: false },
    setVideo: { included: false }
  },
  {
    name: "Artist-collaborated branded video",
    category: 'venue',
    details: {
      title: "Artist-Collaborated Branded Video",
      description: "Collaborate with the artist to create custom audio/visual content featuring both your brand and the performer. Integrated into the show itself, this creates organic brand moments that feel like part of the experience."
    },
    setCore: { included: false },
    setAudio: { included: false },
    setVideo: { included: false }
  },
  {
    name: "On-stage cheers moment",
    category: 'venue',
    details: {
      title: "On-Stage Cheers Moment",
      description: "The artist gives your brand a live shout-out during the performance, creating an authentic endorsement moment. These spontaneous 'cheers' generate excitement and create memorable brand associations."
    },
    setCore: { included: false },
    setAudio: { included: false },
    setVideo: { included: false }
  },
  {
    name: "Branded VIP experience",
    category: 'venue',
    details: {
      title: "Branded VIP Experience",
      description: "Create a custom VIP experience area at the venue branded with your company, including exclusive perks and amenities."
    },
    setCore: { included: false },
    setAudio: { included: false },
    setVideo: { included: false }
  },
  {
    name: "Premium show/stage integration",
    category: 'venue',
    details: {
      title: "Premium Show/Stage Integration",
      description: "Full integration of your brand into the stage design and performance. Includes physical branding on stage elements, lighting effects in brand colors, and coordinated visual moments throughout the show."
    },
    setCore: { included: false },
    setAudio: { included: false },
    setVideo: { included: false }
  },
  {
    name: "Additional show ticket allocation",
    category: 'venue',
    details: {
      title: "Additional Show Ticket Allocation",
      description: "Access to additional complimentary tickets beyond your package allocation for your team, clients, or promotional giveaways."
    },
    setCore: { included: false },
    setAudio: { included: false },
    setVideo: { included: false }
  },
  // Optional Add-Ons (Media/Social)
  {
    name: "Artist social post",
    category: 'media',
    details: {
      title: "Artist Social Post",
      description: "The artist features your brand in posts on their official social media channels (Instagram, TikTok, Twitter/X). Leverage the artist's millions of followers to amplify your brand message with authentic endorsements."
    },
    setCore: { included: false },
    setAudio: { included: false },
    setVideo: { included: false }
  },
  {
    name: "PR extensions",
    category: 'media',
    details: {
      title: "PR Extensions",
      description: "Extend your sponsorship into press releases, media coverage, and public relations campaigns. Co-branded announcements and joint media opportunities generate earned media value and industry buzz."
    },
    setCore: { included: false },
    setAudio: { included: false },
    setVideo: { included: false }
  },
  {
    name: "Artist exclusivity",
    category: 'media',
    details: {
      title: "Artist Exclusivity",
      description: "Secure exclusive rights ensuring the artist does not partner with competing brands in your category during the sponsorship period."
    },
    setCore: { included: false },
    setAudio: { included: false },
    setVideo: { included: false }
  },
  {
    name: "Song licensing",
    category: 'media',
    details: {
      title: "Song Licensing",
      description: "License the artist's music for use in your commercials, digital ads, or branded content. Connect your brand with the emotional power of the artist's songs in your marketing campaigns."
    },
    setCore: { included: false },
    setAudio: { included: false },
    setVideo: { included: false }
  },
  {
    name: "Custom video content",
    category: 'media',
    details: {
      title: "Custom Video Content",
      description: "Collaborate with the artist and production team to create original branded video content. From behind-the-scenes videos to custom shoots, produce unique content that tells your brand story."
    },
    setCore: { included: false },
    setAudio: { included: false },
    setVideo: { included: false }
  },
  {
    name: "Media amplification",
    category: 'media',
    details: {
      title: "Media Amplification",
      description: "Boost your sponsorship reach through paid media support across MAX network channels and partner platforms. Strategic media buys ensure maximum visibility and engagement with target audiences."
    },
    setCore: { included: false },
    setAudio: { included: false },
    setVideo: { included: false }
  },
  {
    name: "Digital sweepstakes",
    category: 'media',
    details: {
      title: "Digital Sweepstakes",
      description: "Run a co-branded digital sweepstakes offering fans chances to win tickets, meet & greets, or exclusive experiences. Drive engagement, collect leads, and create excitement around your brand."
    },
    setCore: { included: false },
    setAudio: { included: false },
    setVideo: { included: false }
  },
  {
    name: "Highlight reel",
    category: 'media',
    details: {
      title: "Highlight Reel",
      description: "Receive a professionally produced highlight reel of your brand activation at the show, perfect for internal presentations and future marketing materials."
    },
    setCore: { included: false },
    setAudio: { included: false },
    setVideo: { included: false }
  },
  {
    name: "Retail integration",
    category: 'media',
    details: {
      title: "Retail Integration",
      description: "Connect your sponsorship to point-of-sale activations and retail partnerships. In-store displays, product tie-ins, and special promotions that drive consumer action at retail locations."
    },
    setCore: { included: false },
    setAudio: { included: false },
    setVideo: { included: false }
  },
  {
    name: "Brand lift study",
    category: 'media',
    details: {
      title: "Brand Lift Study",
      description: "Measure the impact of your sponsorship with a comprehensive brand lift study. Professional market research quantifies changes in brand awareness, perception, and purchase intent among exposed audiences."
    },
    setCore: { included: false },
    setAudio: { included: false },
    setVideo: { included: false }
  }
];

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Type definition for base package tiers (excludes 'custom')
 */
export type BasePackageTier = 'set-core' | 'set-audio' | 'set-video';

/**
 * Get base package information by tier
 * @param tier - The package tier (set-core, set-audio, or set-video)
 * @returns Package name and description
 */
export function getBasePackageInfo(tier: BasePackageTier): {
  name: string;
  description: string;
} {
  switch (tier) {
    case 'set-core':
      return { name: 'SET Core', description: 'Essential features' };
    case 'set-audio':
      return { name: 'SET Audio', description: 'Core + Audio features' };
    case 'set-video':
      return { name: 'SET Video', description: 'Full package features' };
    default:
      return { name: 'SET Core', description: 'Essential features' };
  }
}

/**
 * Get all deliverables included in a base package tier
 * @param tier - The package tier to get deliverables for
 * @returns Array of deliverables included in the specified tier
 */
export function getBasePackageDeliverables(tier: BasePackageTier): DeliverableItem[] {
  return PACKAGE_DELIVERABLES.filter(item => {
    switch (tier) {
      case 'set-core':
        return item.setCore.included;
      case 'set-audio':
        return item.setAudio.included;
      case 'set-video':
        return item.setVideo.included;
      default:
        return false;
    }
  });
}

/**
 * Group deliverables by category
 * @param deliverables - Array of deliverable items to group
 * @returns Object with deliverables grouped by category (mobile, venue, media, standard)
 */
export function groupDeliverablesByCategory(deliverables: DeliverableItem[]) {
  return {
    mobile: deliverables.filter(item => item.category === 'mobile'),
    venue: deliverables.filter(item => item.category === 'venue'),
    media: deliverables.filter(item => item.category === 'media'),
    standard: deliverables.filter(item => item.category === 'standard'),
  };
}